import { HistoricalUtrs, DashboardItemConfig, SurveyUnitConfig } from '@g17eco/types/insight-custom-dashboard';
import { getActualUtrsData } from './utils';
import { sortEffectiveDateAsc } from '@utils/sort';
import './TableWidget.scss';
import { ColumnDef } from '@tanstack/react-table';
import { Table } from '@g17eco/molecules/table';
import { InputColumn } from '@g17eco/types/questionInterfaces';
import { getTableConfiguration } from '@utils/universalTracker';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { UniversalTrackerModalServiceUtrv } from '@g17eco/types/universalTrackerValue';
import { truncate } from '@utils/string';
import { useContext } from 'react';
import { CustomDashboardContext } from '@routes/custom-dashboard/context/CustomDashboardWrapper';
import { ValueListPlain } from '@g17eco/types/valueList';
import { getFormattedValueByColumn } from '@utils/utrvData';

const LIMIT_TRUNCATE_LETTERS = 40;

type DirectTableData = {
  utrv: UniversalTrackerModalServiceUtrv;
  data: {
    [columnCode: string]: string | number | string[] | undefined;
  };
};

export interface DirectTableParams {
  utrsData: HistoricalUtrs[];
  valueListMap: Map<string, ValueListPlain>;
  surveyUnitConfigMap: Map<string, SurveyUnitConfig>;
}

const getTableData = ({
  utrsData,
  valueListMap,
  surveyUnitConfigMap,
  redirectUtrvSource,
  wrapText,
}: DirectTableParams & {
  redirectUtrvSource: (utrv: UniversalTrackerModalServiceUtrv) => void;
  wrapText: boolean;
}) => {
  const actualUtrsData = getActualUtrsData(utrsData);

  // Get the first UTR data for direct table
  const utrData = actualUtrsData[0];

  if (!utrData) {
    return { columns: [], data: [] };
  }

  const tableConfig = getTableConfiguration(utrData.utr);

  if (!tableConfig || !tableConfig.columns) {
    return { columns: [], data: [] };
  }

  const sortedUtrvs = [...utrData.utrvs].sort(sortEffectiveDateAsc);

  const data: DirectTableData[] = sortedUtrvs
    .map((utrv) => {
      const tableRows: InputColumn[][] = utrv.valueData?.table ?? [];
      const surveyId = utrv.compositeData?.surveyId;
      const unitConfig = surveyId ? surveyUnitConfigMap.get(surveyId)?.unitConfig : undefined;

      return tableRows.map((row: InputColumn[]) => {
        const rowData: DirectTableData = {
          utrv,
          data: {},
        };

        row.forEach((cell) => {
          const column = tableConfig.columns.find((c) => c.code === cell.code);
          if (!column) {
            rowData.data[cell.code] = cell.value;
            return;
          }

          const formattedValueByColumn = getFormattedValueByColumn({
            data: row,
            column,
            unitConfig,
            valueListMap,
          });
          rowData.data[cell.code] = formattedValueByColumn;
        });

        return rowData;
      });
    })
    .flat();

  const columns: ColumnDef<DirectTableData>[] = tableConfig.columns.map((column) => ({
    id: column.code,
    header: () => (
      <SimpleTooltip text={column.shortName || column.name}>{column.shortName || column.name}</SimpleTooltip>
    ),
    meta: {
      headerProps: {
        className: 'text-truncate',
        style: {
          maxWidth: 250,
        },
      },
      cellProps: {
        className: 'text-center',
        style: { cursor: 'pointer' },
      },
    },
    cell: ({ row }) => {
      const value = row.original.data[column.code];
      if (value === undefined || value === null) {
        return '';
      }

      const handleCellClick = () => {
        if (row.original.utrv && redirectUtrvSource) {
          redirectUtrvSource(row.original.utrv);
        }
      };

      const displayValue = Array.isArray(value) ? value.join(', ') : String(value);

      if (wrapText) {
        return (
          <span className='d-inline-block' onClick={handleCellClick}>
            {displayValue}
          </span>
        );
      }

      const isTruncated = displayValue.length > LIMIT_TRUNCATE_LETTERS;
      const truncatedValue = truncate(displayValue, LIMIT_TRUNCATE_LETTERS);
      return (
        <SimpleTooltip text={isTruncated ? displayValue : undefined}>
          <span className='d-inline-block' onClick={handleCellClick}>
            {truncatedValue}
          </span>
        </SimpleTooltip>
      );
    },
    enableSorting: false,
  }));

  return {
    columns,
    data,
  };
};

export interface DirectTableWidgetProps extends DirectTableParams {
  config?: DashboardItemConfig;
  itemId?: string; // Add itemId for data-table-item-id attribute
}

export const DirectTableWidget = (props: DirectTableWidgetProps) => {
  const { utrsData, valueListMap, surveyUnitConfigMap, config, itemId } = props;
  const { redirectUtrvSource = () => {} } = useContext(CustomDashboardContext);
  const wrapText = config?.wrapText ?? false;

  if (!utrsData.length) {
    return null;
  }

  const { columns, data } = getTableData({
    utrsData,
    valueListMap,
    surveyUnitConfigMap,
    redirectUtrvSource,
    wrapText,
  });

  return (
    <div
      data-table-item-id={itemId}
      className={`table-widget__container direct-table-widget__container ${wrapText || data.length === 0 ? 'wrapped' : ''}`}
    >
      <Table columns={columns} data={data} pageSize={50} />
      {data.length === 0 ? (
        <p className='table-widget__no-data d-flex align-items-center justify-content-center text-muted bg-white m-0'>
          No data available
        </p>
      ) : null}
    </div>
  );
};
